const RECENT_SEARCHES_KEY = 'theme:recent-searches';
const RECENT_SEARCHES_LIMIT = 5;

function getRecentSearches() {
  try {
    const list = JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY));
    return Array.isArray(list) ? list : [];
  } catch (error) {
    return [];
  }
}

function saveRecentSearch(term, category, categoryUrl) {
  term = (term || '').trim();
  if (!term) return;

  let list = getRecentSearches().filter((entry) => entry.term.toLowerCase() !== term.toLowerCase());
  list.unshift({ term: term, category: category || '', categoryUrl: categoryUrl || '' });
  list = list.slice(0, RECENT_SEARCHES_LIMIT);

  try {
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(list));
  } catch (error) {
    // Ignore write failures (e.g. storage disabled or full)
  }
}

function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value || '';
  return div.innerHTML;
}

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };

    this.recentSearchesWrap = this.querySelector('[data-search-recent]');
    this.recentSearchesList = this.querySelector('[data-search-recent-list]');
    this.renderRecentSearches();

    this.querySelector('form')?.addEventListener('submit', this.handleSearchSubmit.bind(this));
    this.addEventListener('click', this.handleResultClick.bind(this));
  }

  get hasRecommendations() {
    return !this.searchRecommendationEmpty || this.querySelector('.search__trending') !== null;
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    this.renderRecentSearches();

    if (this.isHeaderSearch && this.hasRecommendations) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(false);
    }

    if (this.hasRecommendations) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (!this.hasRecommendations) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    let search_term = this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
    }
    return (
      url.searchParams.set('q', search_term),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(true);
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen && !this.hasRecommendations) {
      this.searchContent?.classList.add('hidden');
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }

  renderRecentSearches() {
    if (!this.recentSearchesWrap || !this.recentSearchesList) return;

    const items = getRecentSearches();
    if (items.length === 0) {
      this.recentSearchesWrap.classList.add('hidden');
      this.recentSearchesList.innerHTML = '';
      return;
    }

    this.recentSearchesList.innerHTML = items
      .map((item) => {
        const searchUrl = `${this.dataset.searchUrl}?type=product&options%5Bprefix%5D=last&q=${encodeURIComponent(item.term)}`;
        const categoryHtml = item.category
          ? `<a href="${escapeHtml(item.categoryUrl || '#')}" class="search__recent-category">in ${escapeHtml(item.category)}</a>`
          : '';
        return `<li class="search__recent-item flex flex-col">
          <a href="${escapeHtml(searchUrl)}" class="search__recent-term reversed-link">${escapeHtml(item.term)}</a>
          ${categoryHtml}
        </li>`;
      })
      .join('');
    this.recentSearchesWrap.classList.remove('hidden');
  }

  handleResultClick(event) {
    const link = event.target.closest('a');
    if (!link || !this.contains(link) || link.closest('[data-search-recent]')) return;

    const term = this.getQuery();
    if (!term) return;

    const productCard = link.closest('.product-card');
    if (productCard) {
      if (link.closest('.product-card__type, .product-card__vendor, .product-card__actions')) return;
      const typeLink = productCard.querySelector('.product-card__type a');
      saveRecentSearch(
        term,
        typeLink ? typeLink.textContent.trim() : '',
        typeLink ? typeLink.getAttribute('href') : ''
      );
    } else if (link.hasAttribute('data-recent-category')) {
      saveRecentSearch(term, link.getAttribute('data-recent-category'), link.getAttribute('data-recent-category-url'));
    } else if (link.closest('#predictive-search-results-queries-list')) {
      saveRecentSearch(term, '', '');
    }
  }

  handleSearchSubmit() {
    const term = this.getQuery();
    if (!term) return;
    const typeLink = this.querySelector('#predictive-search-results-products-list .product-card__type a');
    saveRecentSearch(term, typeLink ? typeLink.textContent.trim() : '', typeLink ? typeLink.getAttribute('href') : '');
  }
}
customElements.define('predictive-search', PredictiveSearch);
